import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardMedia, CardContent, CardActions, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CubeIcon from '@mui/icons-material/ViewInAr';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import { GiHelicopter } from 'react-icons/gi';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/Footer/Footer';
import YachtCard from '../../components/YachtCard/YachtCard';
import ExperienceCard from '../../components/ExperienceCard/ExperienceCard';
import Loading from '../../components/Loading/Loading';
import { db } from '../../config/firebaseConfig';
import { collection, getDocs, limit, query, doc, getDoc, where } from 'firebase/firestore';
import heroBackground from '../../assets/bg.jpg';
import { useAuth } from '../../context/AuthContext';

import featuresImage from '../../assets/features_image.jpeg';
import featuresImage2 from '../../assets/features_image2.jpeg';
import featuresImage3 from '../../assets/features_image3.jpeg';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [featuredYachts, setFeaturedYachts] = useState([]);
  const [featuredExperiences, setFeaturedExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(false);
  const [settings, setSettings] = useState({
    heroTitle: '',
    heroSubtitle: '',
    aboutTitle: '',
    aboutDescription: '',
    featuresTitle: '',
    contactInfo: {
      address: '',
      phone: '',
      email: ''
    },
    features: [],
    faqs: []
  });
  const faqsRef = useRef(null);
  const { user } = useAuth();

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const scrollToFaqs = () => {
    if (faqsRef.current) {
      const headerOffset = 80; // Adjust this value based on your header height
      const elementPosition = faqsRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch settings
        const settingsDoc = await getDoc(doc(db, 'settings', 'homepage'));
        let homepageSettings = {};
        if (settingsDoc.exists()) {
          homepageSettings = settingsDoc.data();
          setSettings(homepageSettings);
        }

        // Fetch featured yachts by IDs from settings
        try {
          const featuredYachtIds = homepageSettings.featuredYachtIds || [];
          if (featuredYachtIds.length > 0) {
            const yachtsList = await Promise.all(
              featuredYachtIds.map(async (yachtId) => {
                const yachtDoc = await getDoc(doc(db, 'yachts', yachtId));
                if (yachtDoc.exists()) {
                  return { id: yachtDoc.id, ...yachtDoc.data() };
                }
                return null;
              })
            );
            setFeaturedYachts(yachtsList.filter(y => y !== null));
          } else {
            // Fallback: fetch first 3 active yachts if no featured IDs set
            const yachtsCollection = collection(db, 'yachts');
            const q = query(yachtsCollection, where('status', '==', 'active'), limit(3));
            const yachtsSnapshot = await getDocs(q);
            const yachtsList = yachtsSnapshot.docs.map(docSnap => ({
              id: docSnap.id,
              ...docSnap.data()
            }));
            setFeaturedYachts(yachtsList);
          }
        } catch (error) {
          console.error('Error fetching featured yachts:', error);
        }

        // Fetch featured experiences by IDs from settings
        try {
          const featuredExperienceIds = homepageSettings.featuredExperienceIds || [];
          let experiencesList = [];
          
          if (featuredExperienceIds.length > 0) {
            experiencesList = await Promise.all(
              featuredExperienceIds.map(async (expId) => {
                const expDoc = await getDoc(doc(db, 'experiences', expId));
                if (expDoc.exists()) {
                  return { id: expDoc.id, ...expDoc.data() };
                }
                return null;
              })
            );
            experiencesList = experiencesList.filter(e => e !== null);
          } else {
            // Fallback: fetch first 3 active experiences if no featured IDs set
            const experiencesCollection = collection(db, 'experiences');
            const expQuery = query(experiencesCollection, where('status', '==', 'active'), limit(3));
            const experiencesSnapshot = await getDocs(expQuery);
            experiencesList = experiencesSnapshot.docs.map(docSnap => ({
              id: docSnap.id,
              ...docSnap.data()
            }));
          }

          // Fetch options for each experience to get pricing
          const experiencesWithOptions = await Promise.all(
            experiencesList.map(async (experience) => {
              const optionsDocRef = doc(db, 'experienceOptions', experience.id);
              const optionsDoc = await getDoc(optionsDocRef);
              
              let options = [];
              if (optionsDoc.exists()) {
                const data = optionsDoc.data();
                options = data.options || [];
              }
              
              return { ...experience, options };
            })
          );

          setFeaturedExperiences(experiencesWithOptions);
        } catch (error) {
          console.error('Error fetching featured experiences:', error);
        }

        setLoading(false);
      }catch (error) {
        console.error('Error fetching settings:', error);
        setLoading(false);
      };
    }
    fetchData();
}, []);

  useEffect(() => {
    // Check if we're coming from another page with the faqs hash
    if (location.hash === '#faqs') {
      // Wait for page to fully render
      setTimeout(scrollToFaqs, 500);
    }
  }, [location]); // This will run when the location changes

  const features = settings.features.length > 0 ? [
    {
      title: settings.features[0].title,
      description: settings.features[0].description,
      action: settings.features[0].action,
      onClick: () => navigate('/explore-yachts'),
      image: featuresImage
    },
    {
      title: settings.features[1].title,
      description: settings.features[1].description,
      action: user ? "Explore" : settings.features[1].action,
      onClick: () => user ? navigate('/explore-yachts') : navigate('/signup'),
      image: featuresImage2
    },
    {
      title: settings.features[2].title,
      description: settings.features[2].description,
      action: settings.features[2].action,
      onClick: () => navigate('/explore-yachts'),
      image: featuresImage3
    }
  ] : [
    {
      title: "Sail with Confidence: Our Crew is Here to Serve You",
      description: "Indulge in top-tier amenities that redefine your sailing experience.",
      action: "Learn More",
      onClick: () => navigate('/about'),
      image: featuresImage
    },
    {
      title: "Flexible Rental Hours to Fit Your Schedule and Preferences",
      description: "Choose your ideal rental duration and enjoy the freedom to explore.",
      action: "Sign Up",
      onClick: () => navigate('/signup'),
      image: featuresImage2
    },
    {
      title: "Explore Our Fleet: Yachts Designed for Comfort and Elegance",
      description: "Select from a variety of luxurious yachts tailored to your needs.",
      action: "View Yachts",
      onClick: () => navigate('/explore-yachts'),
      image: featuresImage3
    }
  ];

  const faqs = settings.faqs.length > 0 ? settings.faqs : [
    {
      question: "How do I book a yacht?",
      answer: "Booking a yacht is simple! Browse our selection, choose your preferred yacht, select your dates, and complete the booking process online. Our team will confirm your reservation within 24 hours."
    },
    {
      question: "What is included in the yacht rental price?",
      answer: "Our yacht rental prices typically include the yacht, crew, fuel for standard cruising, water toys, and basic amenities. Additional services like special catering, extra water sports equipment, or specific route requests may incur additional charges."
    },
    {
      question: "Do I need previous boating experience?",
      answer: "No experience is necessary! All our yachts come with professional crew members who handle navigation and ensure your safety throughout the journey."
    },
    {
      question: "What is the cancellation policy?",
      answer: "We offer full refunds for cancellations made 30 days or more before the scheduled trip. Cancellations within 30 days are subject to our detailed cancellation policy, which you can find in our terms and conditions."
    },
    {
      question: "Can I customize my yacht charter experience?",
      answer: "Absolutely! We can customize every aspect of your charter experience, from the itinerary and menu to special occasions and specific activities. Contact our team to discuss your preferences."
    }
  ];

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height:"100vh", width:"100vw" }}><Loading /></Box>;
  }

  return (
    <Box>
      <Helmet>
        <title>Bosphorus Yachts | Private Yacht Rental & Experiences in Istanbul</title>
        <meta name="description" content="Experience the beauty of Istanbul from the water with private yacht rentals and curated group experiences on the Bosphorus. Professional crews, luxury yachts, unforgettable views. Starting from €39/person." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Bosphorus Yachts | Private Yacht Rental & Experiences in Istanbul" />
        <meta property="og:description" content="Private yacht charters and group experiences on the Bosphorus. Luxury yachts with professional crews. Starting from €39/person." />
        <meta property="og:image" content="https://www.bosphorusyachts.com/preview_image.png" />
        <meta property="og:url" content="https://www.bosphorusyachts.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bosphorus Yachts | Private Yacht Rental & Experiences in Istanbul" />
        <meta name="twitter:description" content="Private yacht charters and group experiences on the Bosphorus. Starting from €39/person." />
        <meta name="twitter:image" content="https://www.bosphorusyachts.com/preview_image.png" />
        <link rel="canonical" href="https://www.bosphorusyachts.com" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            "name": "Bosphorus Yachts",
            "description": "Private yacht rentals and curated group experiences on the Bosphorus strait in Istanbul, Turkey.",
            "url": "https://www.bosphorusyachts.com",
            "image": "https://www.bosphorusyachts.com/preview_image.png",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Istanbul",
              "addressCountry": "TR"
            },
            "priceRange": "€€",
            "sameAs": []
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
          padding: '2rem'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" sx={{ fontSize: { xs: '2.1rem', md: '4rem' }, fontWeight: 'bold', mb: 3 }}>
            {settings.heroTitle}
          </Typography>
          <Typography variant="h5" sx={{ fontSize: { xs: '1.1rem', md: '2rem' }, mb: 4, maxWidth: '800px', mx: 'auto' }}>
            {settings.heroSubtitle}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center', flexDirection: 'column'}}>
            <Box
              onClick={() => navigate('/explore-yachts')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'rgba(255,255,255,0.9)',
                borderRadius: '50px',
                pl: 0.5,
                pr: 4,
                py: 0.5,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  bgcolor: 'white',
                }
              }}
            >
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#2C7D89',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <DirectionsBoatIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography sx={{ color: '#333', fontWeight: 500, fontSize: '1.1rem' }}>
                Explore Yachts
              </Typography>
            </Box>
            <Box
              onClick={() => navigate('/experiences')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'rgba(255,255,255,0.9)',
                borderRadius: '50px',
                pl: 0.5,
                pr: 4,
                py: 0.5,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  bgcolor: 'white',
                }
              }}
            >
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#9C27B0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <GiHelicopter style={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography sx={{ color: '#333', fontWeight: 500, fontSize: '1.1rem' }}>
                Explore Experiences
              </Typography>
            </Box>
            {user ? null : <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                px: 4,
                py: 1,
                borderRadius: '50px',
              }}
            >
              Sign Up
            </Button>}
          </Box>
        </Container>
      </Box>

      {/* Featured Experiences Section */}
      <Box sx={{ py: 8, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" sx={{ mb: 4, fontSize: { xs: '2rem', md: '3rem' } }}>
            Featured Experiences
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3, justifyContent: 'center' }}>
            {featuredExperiences.map((experience) => (
              <Grid item xs={12} md={6} lg={4} key={experience.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                <ExperienceCard experience={experience} />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/experiences')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                backgroundColor: '#9C27B0',
                '&:hover': {
                  backgroundColor: '#7B1FA2',
                }
              }}
            >
              Explore All Experiences
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Featured Yachts Section */}
      <Box sx={{ py: 8, bgcolor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" sx={{ mb: 4, fontSize: { xs: '2rem', md: '3rem' } }}>
            Private Yachts
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3, alignItems: 'center', justifyContent: 'center' }}>
            {featuredYachts.map((yacht) => (
              <Grid item xs={12} md={6} lg={4} key={yacht.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <YachtCard yacht={yacht} />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/explore-yachts')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              Explore All Yachts
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 8, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" sx={{ mb: 6, fontSize: { xs: '2rem', md: '3rem' } }}>
            {settings.featuresTitle}
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  textAlign: 'center', 
                  p: 3, 
                  height: '100%',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                  }
                }}>
                  <Box>
                    <img 
                      src={feature.image} 
                      alt={feature.title} 
                      width="80"
                      height="80"
                      loading="lazy"
                      style={{ 
                        width: '80px', 
                        height: '80px', 
                        borderRadius: '50%',
                        objectFit: 'cover',
                        marginBottom: '16px'
                      }}
                    />
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Typography sx={{ mb: 3, color: 'text.secondary' }}>
                      {feature.description}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={feature.onClick}
                      sx={{ color: '#2C7D89' }}
                    >
                      {feature.action} →
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQs Section */}
      <Box 
        id="faqs" 
        ref={faqsRef} 
        sx={{ 
          py: 8, 
          bgcolor: 'white',
          scrollMarginTop: '80px' // This helps with scroll positioning
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 6,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 'bold'
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFaq === `panel${index}`}
                onChange={handleFaqChange(`panel${index}`)}
                sx={{
                  mb: 2,
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  '&:not(:last-child)': { mb: 2 },
                  '&.Mui-expanded': {
                    borderRadius: '8px !important',
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      my: 2
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default HomePage;
